import { ReactNode, useEffect, useState } from 'react';

import { MemoryRouter } from 'react-router';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import * as Sentry from '@sentry/react';
import posthog from 'posthog-js';

import { ModalProvider, ModalWrapper } from '@app-velocity/ui';
import { RequestGateway } from '@app-velocity/request-gateway';

import { PageViewTracker, AppContext } from '@app-velocity/config';

import { ClientTokenStorage } from './ClientTokenStorage';
import { ApiConfig } from '../core/types';
import { getCoreVersion, loadPage } from '../core/methods';
import { AppVersionControl } from './AppVersionControl';

type Props = {
  children: ReactNode;
};

const clientStorage = new ClientTokenStorage();
const queryClient = new QueryClient();

posthog.init(import.meta.env.VITE_POSTHOG_API_KEY, {
  api_host: import.meta.env.VITE_PUBLIC_POSTHOG_HOST,
  capture_pageview: false,
  person_profiles: 'identified_only',
  session_recording: {
    maskAllInputs: false,
    maskInputOptions: {
      password: true,
    },
  },
});

const apiUrl = import.meta.env.VITE_FIGMA_PLUGIN_API_URL;
const AVMLink = import.meta.env.VITE_FIGMA_PLUGIN_MAIN_APP_LINK;
const scenarioExample = import.meta.env.VITE_FIGMA_PLUGIN_EXAMPLE_PLAYGROUND;
const scenarioDemo = import.meta.env.VITE_FIGMA_PLUGIN_DEMO;

const config: ApiConfig = {
  apiUrl,
  AVMLink,
  scenarioDemo,
  scenarioExample,
};

export const AppProviders = ({ children }: Props) => {
  const [coreVersion, setCoreVersion] = useState('');

  const getBuildCoreVersion = async () => {
    const version = await getCoreVersion();
    setCoreVersion(version);
  };

  const initSentry = async () => {
    const coreVersion = await getCoreVersion();
    const uiVersion = BUILD_VERSION;

    Sentry.init({
      dsn: 'https://7bec86d213d5ef25f88f603c7ca28b15@o4508648267055104.ingest.de.sentry.io/4508652187222096',
      // integrations: [
      //   Sentry.replayIntegration({
      //     maskAllText: false,
      //     maskAllInputs: false,
      //     mask: ['[type="password"]'],
      //   }),
      // ],
      environment: import.meta.env.VITE_ENV,
      tracesSampleRate: 1.0,
      replaysSessionSampleRate: 1.0,
      replaysOnErrorSampleRate: 1.0,
      release: `${uiVersion};${coreVersion}`,
    });
  };

  useEffect(() => {
    getBuildCoreVersion();
    loadPage();
    initSentry();
  }, []);

  const { apiUrl, AVMLink, scenarioDemo, scenarioExample } = config;

  return (
    <QueryClientProvider client={queryClient}>
      <MemoryRouter>
        <AppContext.Provider
          value={{
            apiUrl,
            AVMLink,
            scenarioDemo: scenarioDemo || '',
            scenarioExample: scenarioExample || '',
          }}
        >
          <ModalProvider>
            <ModalWrapper />
            <RequestGateway
              baseUrl={config.apiUrl}
              tokenStorage={clientStorage}
              refreshTokenUrl="/v2/auth/token/refresh"
              version={`${BUILD_VERSION};${coreVersion}`}
              source={BUILD_SOURCE}
            >
              <AppVersionControl>{children}</AppVersionControl>
              <PageViewTracker />
            </RequestGateway>
          </ModalProvider>
        </AppContext.Provider>
      </MemoryRouter>
    </QueryClientProvider>
  );
};
